<script setup>
import { onMounted, ref } from 'vue';
import UsersList from '../components/UsersList.vue';
import { useClientsStore } from '../stores/clients';

const clientsStore = useClientsStore();

const expiringContracts = ref([]);
const birthdaysThisWeek = ref([]);
const loadingContracts = ref(true);
const loadingBirthdays = ref(true);
const errorMessage = ref('');
const showAllContracts = ref(false);
const showAllBirthdays = ref(false);

const formatDate = (dateStr) => {
	if (!dateStr) return '';
	return new Date(dateStr).toLocaleDateString('fr-FR');
};

const fetchExpiringContracts = () => {
	loadingContracts.value = true;
	errorMessage.value = '';
	clientsStore.getContractsEndingThisWeek()
		.then(res => {
			expiringContracts.value = res.clients ?? [];
		})
		.catch(() => {
			errorMessage.value = 'Impossible de récupérer les contrats qui se terminent cette semaine.';
		})
		.finally(() => {
			loadingContracts.value = false;
		});
};

const fetchBirthdays = () => {
	loadingBirthdays.value = true;
	errorMessage.value = '';
	clientsStore.getBirthdaysOfWeek()
		.then(res => {
			birthdaysThisWeek.value = res.clients ?? [];
		})
		.catch(() => {
			errorMessage.value = 'Impossible de récupérer les anniversaires de la semaine.';
		})
		.finally(() => {
			loadingBirthdays.value = false;
		});
};

onMounted(() => {
	fetchExpiringContracts();
	fetchBirthdays();
});
</script>

<template>
	<div class="pb-24 overflow-auto h-screen">
		<div class="max-w-7xl mx-auto space-y-6">
			<div v-if="errorMessage" class="rounded-md bg-rose-50 p-4 text-sm text-rose-700 border border-rose-100">
				{{ errorMessage }}
			</div>

			<div class="mt-6 grid gap-6 lg:grid-cols-1">
				<section class="bg-white shadow rounded-lg p-6">
					<div class="flex items-center justify-between mb-4">
						<h2 class="text-lg font-semibold text-gray-900">Contrats qui se terminent cette semaine</h2>
						<span v-if="loadingContracts" class="text-sm text-gray-500">Chargement...</span>
					</div>
					<ul v-if="!loadingContracts && expiringContracts.length" class="divide-y divide-gray-200">
						<li v-for="contract in (showAllContracts ? expiringContracts : expiringContracts.slice(0,5))" :key="contract.id_cli" class="py-3 flex items-start justify-between">
							<div>
								<p class="text-sm font-medium text-gray-900">{{ contract.pNoms_cli }} {{ contract.nom_cli }}</p>
								<p class="text-xs text-gray-500">Fin de contrat&nbsp;: {{ formatDate(contract.contract_end_date) }}</p>
							</div>
							<span class="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
								J{{ Math.round(contract.days_until_end) }}
							</span>
						</li>
					</ul>
					<div v-if="!loadingContracts && expiringContracts.length > 5" class="mt-3">
						<button type="button" @click="showAllContracts = !showAllContracts" class="text-sm text-rose-600 hover:text-rose-700 font-medium">
							{{ showAllContracts ? 'Voir moins' : 'Voir plus' }}
						</button>
					</div>
					<p v-else-if="!loadingContracts" class="text-sm text-gray-500">Aucun autre contrat n'expire cette semaine.</p>
				</section>

				<section class="bg-white shadow rounded-lg p-6">
					<div class="flex items-center justify-between mb-4">
						<h2 class="text-lg font-semibold text-gray-900">Anniversaires de la semaine</h2>
						<span v-if="loadingBirthdays" class="text-sm text-gray-500">Chargement...</span>
					</div>
					<ul v-if="!loadingBirthdays && birthdaysThisWeek.length" class="divide-y divide-gray-200">
						<li v-for="client in (showAllBirthdays ? birthdaysThisWeek : birthdaysThisWeek.slice(0,5))" :key="client.id_cli" class="py-3 flex items-start justify-between">
							<div>
								<p class="text-sm font-medium text-gray-900">{{ client.pNoms_cli }} {{ client.nom_cli }}</p>
								<p class="text-xs text-gray-500">Anniversaire&nbsp;: {{ formatDate(client.next_birthday) }}</p>
							</div>
							<span class="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
								{{ client.turning_age }} ans
							</span>
						</li>
					</ul>
					<div v-if="!loadingBirthdays && birthdaysThisWeek.length > 5" class="mt-3">
						<button type="button" @click="showAllBirthdays = !showAllBirthdays" class="text-sm text-rose-600 hover:text-rose-700 font-medium">
							{{ showAllBirthdays ? 'Voir moins' : 'Voir plus' }}
						</button>
					</div>
					<p v-else-if="!loadingBirthdays" class="text-sm text-gray-500">Aucun autre anniversaire cette semaine.</p>
				</section>
			</div>

			<div class="flex w-full bg-gradient-to-b from-white/80 to-white/0 relative bg-white shadow rounded-lg">
				<UsersList />
				<!-- <Feed /> -->
			</div>
		</div>
	</div>
</template>
